import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { DataGrid, DataGridLayout } from '@verantos-dev/shared-components';

import { useMappingResultsColumns } from '../hooks/useMappingResultsColumns';
import { useMappingResultsDataGrid } from '../hooks/useMappingResultsDataGrid';
import type { MappingResult } from '../types';

/**
 * MappingResultsGrid Component
 *
 * Displays the detailed mapping results in a DataGrid with client-side pagination, sorting, and filtering.
 * Features:
 * - Expandable rows for mapped concepts
 * - Color-coded error cells for unmapped/not found items
 * - Client-side sorting, filtering, and pagination
 * - Client-side mode (all data loaded upfront)
 */

interface MappingResultsGridProps {
  /** The mapping results to display */
  results: MappingResult[];
  /** Fill the parent's height (draft review tabs) instead of the fixed 70vh. */
  fillHeight?: boolean;
}

// No-op function for client-mode DataGrid
const noOpFetch = () => {
  // This is intentionally empty for client-mode DataGrid
};

export const MappingResultsGrid = ({ results, fillHeight = false }: MappingResultsGridProps) => {
  const theme = useTheme();

  // Get column definitions from custom hook
  const columns = useMappingResultsColumns();

  // Get DataGrid configuration (rows, detail panels, styling) from custom hook
  const { rows, getDetailPanelContent, getDetailPanelHeight, getRowClassName } =
    useMappingResultsDataGrid(results, columns);

  // fillHeight: the draft review tabs size this grid to the remaining tab
  // height (matching the Definition/Concepts tabs) instead of a fixed 70vh.
  return (
    <Box sx={{ height: fillHeight ? '100%' : '70vh', width: '100%' }}>
      <DataGridLayout id="mapping-results-datagrid-layout">
        <DataGrid
          key={`datagrid-${rows.length}`}
          id="mapping-results"
          mode="client"
          rows={rows}
          columns={columns}
          getRowId={(row) => row.rowId}
          useFetchDataGridData={noOpFetch}
          pageSizeOptions={[10, 25, 50, 100]}
          initialPageSize={25}
          pagination
          disableSearchParamSync
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={getDetailPanelHeight}
          getRowClassName={getRowClassName}
          getCellClassName={() => {
            return '';
          }}
          slots={{
            detailPanelExpandIcon: ChevronRightIcon,
            detailPanelCollapseIcon: ExpandMoreIcon,
          }}
          sx={{
            '& .non-collapsible .MuiDataGrid-detailPanelToggleCell': {
              display: 'none',
            },
            '& .MuiDataGrid-detailPanelToggleCell': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
            '& .MuiDataGrid-detailPanel': {
              padding: 0,
              margin: 0,
              width: '100%',
            },
            '& .MuiDataGrid-cell': {
              fontSize: theme.typography.body2.fontSize,
              fontWeight: theme.typography.body2.fontWeight,
              color: theme.palette.text.primary,
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
            '& .MuiDataGrid-row': {
              '&:nth-of-type(even)': {
                backgroundColor: theme.palette.action.hover,
              },
            },
            // Unmapped / not-found source rows in red (overrides the zebra stripe),
            // matching the nested mapped-rows grid so excluded codes are obvious.
            '& .MuiDataGrid-row.error-row': {
              backgroundColor: `${theme.palette.error.light} !important`,
              '&:hover': {
                backgroundColor: `${theme.palette.error.main} !important`,
              },
            },
            '& .MuiDataGrid-columnHeaders': {
              fontSize: theme.typography.subtitle1.fontSize,
              fontWeight: 700,
              borderBottom: 'none',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              overflow: 'visible',
              whiteSpace: 'normal',
              lineHeight: theme.typography.subtitle1.lineHeight,
              textOverflow: 'unset',
            },
            '& .MuiDataGrid-menuIconButton': {
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            },
            '& .MuiDataGrid-footerContainer': {
              minHeight: 52,
              height: 52,
              '& .MuiTypography-root': {
                color: theme.palette.text.primary,
              },
            },
          }}
          slotProps={{
            columnHeaders: {
              style: {
                lineHeight: 'normal',
                padding: '8px 16px',
                fontWeight: 700,
                fontSize: theme.typography.subtitle1.fontSize,
              },
            },
          }}
        />
      </DataGridLayout>
    </Box>
  );
};
